import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useRouteParams } from "@/hooks/useEncodedIds";
import Alert from "@/components/Alert";
import { Button } from "@/components/Button";
import Layout from "@/components/Layout";
import { Spinner } from "@/components/Spinner";
import useLivlyUser from "@/context/UserProvider";
import { useChecklist } from "@/context/ChecklistProvider";
import {
  SurveyAnswer,
  SurveyQuestionCard,
  Track,
  buildSurveyResponsePayload,
  isQuestionAnswered,
  postSurveyResponse,
  sortQuestions,
  useSurveyDetails,
} from "../survey";

const backTarget = { label: "Move-in Checklist", to: ".." };

// keyed by lease + survey so progress resumes if the resident exits mid-flow
function getProgressStorageKey(leaseId: number, surveyId: number) {
  return `onboarder-survey-progress-${leaseId}-${surveyId}`;
}

export default function ChecklistOnboarderSurveyPage() {
  const params = useRouteParams<{ leaseId: string; surveyId: string }>();
  const navigate = useNavigate();
  const { user } = useLivlyUser();
  const leaseId = Number(params.leaseId);
  const surveyId = Number(params.surveyId);
  const storageKey = getProgressStorageKey(leaseId, surveyId);
  const { refetch: refetchChecklist } = useChecklist();

  const { data, isLoading, isError } = useSurveyDetails(leaseId, surveyId);
  const questions = useMemo(
    () => sortQuestions(data?.questions),
    [data?.questions]
  );
  const total = questions.length;

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, SurveyAnswer>>({});

  useEffect(() => {
    if (!leaseId || !surveyId) {
      return;
    }

    const saved = localStorage.getItem(storageKey);
    if (!saved) {
      return;
    }

    try {
      const parsed = JSON.parse(saved) as {
        step: number;
        answers: Record<number, SurveyAnswer>;
      };
      setStep(parsed.step ?? 0);
      setAnswers(parsed.answers ?? {});
    } catch {
      localStorage.removeItem(storageKey);
    }
  }, [leaseId, surveyId, storageKey]);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify({ step, answers }));
  }, [step, answers, storageKey]);

  // no need to resume in-progress answers once the resident has already responded
  useEffect(() => {
    if (data?.hasResponded) {
      localStorage.removeItem(storageKey);
    }
  }, [data?.hasResponded, storageKey]);

  const question = questions[step];
  const questionId = question?.surveyQuestionId ?? 0;
  const answer = answers[questionId];
  const isLastQuestion = step === total - 1;
  const isCurrentQuestionValid = isQuestionAnswered(question, answer);

  const submitResponseMutation = useMutation({
    mutationFn: () =>
      postSurveyResponse(
        leaseId,
        surveyId,
        buildSurveyResponsePayload(questions, answers)
      ),
    onSuccess: async () => {
      localStorage.removeItem(storageKey);
      // onboarder survey completion is derived server-side from the survey response, not posted
      refetchChecklist();
      navigate("..");
    },
  });

  const onSetAnswer = (next: SurveyAnswer) => {
    if (!questionId) {
      return;
    }

    setAnswers((current) => ({ ...current, [questionId]: next }));
  };

  const onDoneAlreadyResponded = () => {
    navigate("..");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center flex-1 my-56">
        <Spinner color="livly" size="xl" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Layout title="Survey" back={backTarget}>
        <Alert message="There was an error loading the survey. Please try again." />
      </Layout>
    );
  }

  if (data.hasResponded) {
    return (
      <Layout title={data.title ?? "Move in Survey"} back={backTarget}>
        <div className="flex flex-col items-center px-4 pt-16 text-center">
          <h2 className="text-2xl font-dm-serif">Survey already completed</h2>
          <p className="mt-3 max-w-sm text-sm text-gray-400">
            You've already submitted a response to this survey.
          </p>
          <Button
            color="secondary"
            className="mt-10 w-full max-w-md"
            onClick={onDoneAlreadyResponded}
          >
            Done
          </Button>
        </div>
      </Layout>
    );
  }

  if (!question) {
    return (
      <Layout title={data.title ?? "Move in Survey"} back={backTarget}>
        <div className="max-w-2xl">
          <Alert
            variant="default"
            message="This survey does not have any questions yet."
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={data.title ?? "Move in Survey"} back={backTarget}>
      <div className="max-w-3xl pb-36">
        {total > 1 && <Track step={step} total={total} />}

        <div className="mt-6 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="px-5 py-6">
            {submitResponseMutation.isError && (
              <div className="mb-4">
                <Alert message="Unable to submit survey right now. Please try again." />
              </div>
            )}
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">
              Question {step + 1}/{total}
            </p>
            <div className="mt-3 flex items-start gap-2">
              <h2 className="text-lg font-normal leading-tight text-gray-900">
                {question.questionText}
              </h2>
              {question.isRequired ? (
                <span className="mt-1 text-sm font-medium text-[#F9877E]">
                  *
                </span>
              ) : null}
            </div>

            <div className="mt-6">
              <SurveyQuestionCard
                question={question}
                answer={answer}
                onAnswerChange={onSetAnswer}
                timezone={user?.building?.timezone}
              />
            </div>
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 flex flex-col items-stretch m-4 bg-white border border-gray-100 rounded-lg shadow-lg md:flex-row md:justify-between md:items-center md:left-64">
          <div className="flex flex-col items-center justify-end w-full gap-3 p-4 border-t border-gray-200 md:flex-row md:border-none">
            {step !== 0 && (
              <Button
                color="primary"
                outlined
                onClick={() => setStep((current) => current - 1)}
                className="w-full md:w-auto"
              >
                Previous
              </Button>
            )}

            {isLastQuestion ? (
              <Button
                color="secondary"
                disabled={
                  !isCurrentQuestionValid || submitResponseMutation.isLoading
                }
                onClick={() => submitResponseMutation.mutate()}
                className="w-full md:w-auto"
              >
                {submitResponseMutation.isLoading ? "Submitting..." : "Submit"}
              </Button>
            ) : (
              <Button
                color="secondary"
                onClick={() => setStep((current) => current + 1)}
                disabled={!isCurrentQuestionValid}
                className="w-full md:w-auto"
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
